import React, { useEffect, useState, useRef } from "react";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import { HeaderTitle } from "../common/titles";
import { GET_PRODUCT_COUNT, GET_PRODUCT_LIST } from "./queries";
import { useLazyQuery, useQuery } from "@apollo/client";
import { Product } from "../../../models/product_model";
import { useHistory } from "react-router-dom";
import Pagination from "./pagination";
import { GETPRODUCTS } from "../orders/queries";
import { has_access } from "../../../middleware/privileges";

const PAGE_SIZE = 20;

const btn = {
  txt: lang("Nuevo Producto", "New Product"),
  icon: "fa fa-plus",
  link: "/products/new",
};

type Category = { id: string; name: string };

type OrderByOption = {
  label: string;
  value: string;
};

const orderByOptions: OrderByOption[] = [
  { label: lang("# ID", "# ID"), value: "id_ASC" },
  { label: lang("Nombre A-Z", "Name A-Z"), value: "title_ASC" },
  { label: lang("Nombre Z-A", "Name Z-A"), value: "title_DESC" },
  { label: lang("Precio menor", "Price low"), value: "price_ASC" },
  { label: lang("Precio mayor", "Price high"), value: "price_DESC" },
];

const formatProductPrice = (product: Product): string => {
  if (!product.has_size) return `$${product.price}`;

  if (!product.sizes || product.sizes.length === 0) {
    return lang("Precio por tamaños", "Priced by size");
  }

  const prices = product.sizes.map((size) => size.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);

  return min === max ? `$${min}` : `$${min}-${max}`;
};

const ProductList: React.FC = () => {
  const history = useHistory();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<number>(0);
  const [availabilityFilter, setAvailabilityFilter] = useState<string>("");
  const [orderBy, setOrderBy] = useState("id_ASC");
  const searchRef = useRef<HTMLInputElement>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>();

  const { data: productsDb } = useQuery(GETPRODUCTS, {
    fetchPolicy: "no-cache",
  });

  const filterVars = {
    search: debouncedSearch || undefined,
    category: categoryFilter || undefined,
    snoozed:
      availabilityFilter === "snoozed"
        ? true
        : availabilityFilter === "available"
        ? false
        : undefined,
    status_web:
      availabilityFilter === "web"
        ? true
        : availabilityFilter === "no_web"
        ? false
        : undefined,
  };

  const [fetchCount, { data: getProductCount }] = useLazyQuery(
    GET_PRODUCT_COUNT,
    { fetchPolicy: "no-cache" }
  );
  const [fetchProducts, { data, loading }] = useLazyQuery(GET_PRODUCT_LIST, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    const numToSkip = (page - 1) * PAGE_SIZE;
    fetchProducts({
      variables: {
        take: PAGE_SIZE,
        skip: numToSkip,
        orderBy,
        ...filterVars,
      },
    });
    fetchCount({ variables: filterVars });
    //eslint-disable-next-line
  }, [page, debouncedSearch, categoryFilter, availabilityFilter, orderBy]);

  useEffect(() => {
    if (productsDb) {
      const categories = [...productsDb.getCategories].sort(
        (a: Category, b: Category) => (a.name > b.name ? 1 : -1)
      );
      const menu_obj = {
        products: [...productsDb.getProducts],
        categories,
      };
      localStorage.setItem("menu_all", JSON.stringify(menu_obj));
    }
  }, [productsDb]);

  const handleSearch = (value: string) => {
    setSearch(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 400);
  };

  const clearFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setCategoryFilter(0);
    setAvailabilityFilter("");
    setOrderBy("id_ASC");
    setPage(1);
  };

  const hasActiveFilters =
    debouncedSearch || categoryFilter || availabilityFilter || orderBy !== "id_ASC";

  const categories: Category[] = productsDb?.getCategories
    ? [...productsDb.getCategories].sort((a: Category, b: Category) =>
        a.name > b.name ? 1 : -1
      )
    : [];

  return (
    <>
      <HeaderTitle
        title={lang("Productos", "Products")}
        btn={has_access(256) ? btn : null}
      />
      <div className="row">
        <div className="col-lg-12">
          {/* Filters */}
          <div className="material_card p-3 mb-3">
            <div className="row align-items-end">
              {/* Search */}
              <div className="col-lg-4 col-md-6 mb-2">
                <label className="small text-muted mb-1">
                  {lang("Buscar", "Search")}
                </label>
                <div className="input-group">
                  <div className="input-group-prepend">
                    <span className="input-group-text">
                      <i className="fa fa-search"></i>
                    </span>
                  </div>
                  <input
                    ref={searchRef}
                    type="text"
                    className="form-control"
                    placeholder={lang(
                      "Nombre o SKU...",
                      "Name or SKU..."
                    )}
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {search && (
                    <div className="input-group-append">
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          handleSearch("");
                          searchRef.current?.focus();
                        }}
                      >
                        <i className="fa fa-times"></i>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Category filter */}
              <div className="col-lg-3 col-md-6 mb-2">
                <label className="small text-muted mb-1">
                  {lang("Tipo", "Category")}
                </label>
                <select
                  className="custom-select"
                  value={categoryFilter}
                  onChange={(e) => {
                    setCategoryFilter(+e.target.value);
                    setPage(1);
                  }}
                >
                  <option value={0}>{lang("Todos", "All")}</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Availability filter */}
              <div className="col-lg-3 col-md-6 mb-2">
                <label className="small text-muted mb-1">
                  {lang("Disponibilidad", "Availability")}
                </label>
                <select
                  className="custom-select"
                  value={availabilityFilter}
                  onChange={(e) => {
                    setAvailabilityFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{lang("Todos", "All")}</option>
                  <option value="available">
                    {lang("Disponibles", "Available")}
                  </option>
                  <option value="snoozed">
                    {lang("Pausados", "Snoozed")}
                  </option>
                  <option value="web">
                    {lang("Visibles en web", "Visible on web")}
                  </option>
                  <option value="no_web">
                    {lang("Ocultos en web", "Hidden on web")}
                  </option>
                </select>
              </div>

              {/* Order by */}
              <div className="col-lg-2 col-md-6 mb-2">
                <label className="small text-muted mb-1">
                  {lang("Ordenar", "Sort")}
                </label>
                <select
                  className="custom-select"
                  value={orderBy}
                  onChange={(e) => {
                    setOrderBy(e.target.value);
                    setPage(1);
                  }}
                >
                  {orderByOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {hasActiveFilters && (
              <div className="mt-2">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={clearFilters}
                >
                  <i className="fa fa-times mr-1"></i>
                  {lang("Limpiar filtros", "Clear filters")}
                </button>
                {getProductCount && (
                  <span className="ml-2 text-muted small">
                    {getProductCount.getProductCount}{" "}
                    {lang("resultados", "results")}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Table */}
          <div className="material_card p-3 table-responsive">
            {loading && !data ? (
              <LoaderDots fullSize />
            ) : !data?.getProducts?.length ? (
              <div className="text-center py-4 text-muted">
                <i className="fas fa-box-open fa-2x mb-2 d-block"></i>
                {debouncedSearch
                  ? lang(
                      `No se encontraron productos para "${debouncedSearch}"`,
                      `No products found for "${debouncedSearch}"`
                    )
                  : lang("No hay productos", "No products")}
              </div>
            ) : (
              <table className="table table-hover product_table vertical_middle">
                <thead className="thead-light">
                  <tr>
                    <th scope="col">#</th>
                    <th scope="col">{lang("Producto", "Product")}</th>
                    <th scope="col">{lang("Precio", "Price")}</th>
                    <th scope="col">{lang("Tipo", "Category")}</th>
                    <th scope="col">{lang("Estado", "Status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.getProducts.map((product: Product) => (
                    <tr
                      key={product.id}
                      className="filter_list"
                      onClick={() =>
                        history.push(`/products/edit/${product.id}`)
                      }
                    >
                      <th scope="row">#{product.id}</th>
                      <td>
                        <img
                          src={
                            product.images.length
                              ? product.images[0].url
                                  .replace(
                                    "ftp-foo.s3.amazonaws.com",
                                    "cdn.foopos.mx"
                                  )
                                  .replace(
                                    "s3.amazonaws.com/ftp-foo",
                                    "cdn.foopos.mx"
                                  )
                              : "https://s3.amazonaws.com/ftp-foo/page_assets/no_photo_04.jpg"
                          }
                          className="img-thumbnail xs_hidden mr-1"
                          alt={product.title}
                        />
                        {product.title}
                      </td>
                      <td>{formatProductPrice(product)}</td>
                      <td>
                        <span className="text-capitalize">
                          {product.category?.name}
                        </span>
                      </td>
                      <td>
                        {product.snoozed ? (
                          <span className="badge badge-warning">
                            {lang("Pausado", "Snoozed")}
                          </span>
                        ) : (
                          <span className="badge badge-success">
                            {lang("Activo", "Active")}
                          </span>
                        )}
                        {product.status_web ? (
                          <span className="badge badge-info ml-1">Web</span>
                        ) : (
                          <span className="badge badge-secondary ml-1">
                            {lang("Sin web", "No web")}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <Pagination
            onClick={(p) => setPage(p)}
            pageSize={PAGE_SIZE}
            page={page}
            totalCount={
              getProductCount ? getProductCount.getProductCount : 0
            }
          />
        </div>
      </div>
    </>
  );
};

export default ProductList;
